import React from "react";
import PropTypes from "prop-types";
import {
  Breadcrumb,
  BreadcrumbItem,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";
import { isBlankOrHangulFiller } from "../../../utils";

const mapRootFile = sourceFileName => ({
  item: sourceFileName,
  path: "/",
  title: `Browse /`,
  r: 0,
  childRoot: false
});

const getBreadcrumbItemTestId = (testId, index, lastPath) => {
  if (!testId) return null;
  if (index === 0) {
    return `${testId}_root_item`;
  } else if (lastPath) {
    return `${testId}_last_item`;
  }
  return `${testId}_item_${index}`;
};

const getFileNameDisplay = data => {
  if (isBlankOrHangulFiller(data.item)) {
    return data.hashValue;
  }
  return data.item;
};

const mapFetchedRelationshipPaths = (
  fetchedRelationshipPaths,
  sampleRelationshipId,
  sourceHash
) => {
  let fetchedRelationshipBreadcrumbItems = [];
  fetchedRelationshipPaths.forEach((relationshipPath, parentIndex) => {
    const { path, r, hash_value } = relationshipPath || {};
    const relationshipPathArray = path.split("/") || [];

    fetchedRelationshipBreadcrumbItems = [
      ...fetchedRelationshipBreadcrumbItems,
      ...relationshipPathArray.map((p, index) => {
        let relationshipId = null;
        let hashValue = hash_value;
        let childRoot = false;
        let title = `Browse ${relationshipPathArray
          .slice(0, index + 1)
          .join("/")}`;

        if (
          relationshipPathArray.length - 1 === index &&
          fetchedRelationshipPaths.length - 1 !== parentIndex
        ) {
          const element = fetchedRelationshipPaths[parentIndex + 1];
          relationshipId = element.r;
          hashValue = element.hash_value;
          childRoot = true;
        } else if (
          relationshipPathArray.length - 1 === index &&
          fetchedRelationshipPaths.length - 1 === parentIndex
        ) {
          title = (
            <span>
              This sample is an Extracted File received during Static Analysis
              of:{" "}
              <span className="last_item_breadcrumb_tooltip">
                {sourceHash.slice(0, 12)}...
              </span>
            </span>
          );
        } else {
          relationshipId = r;
        }

        return {
          item: p,
          path:
            relationshipPathArray.length - 1 === index
              ? "/"
              : relationshipPathArray.slice(0, index + 1).join("/"),
          title,
          r: relationshipId,
          hashValue,
          childRoot
        };
      })
    ];
  });
  return fetchedRelationshipBreadcrumbItems;
};

const mapCurrentFilePath = (currentPath, sampleRelationshipId) => {
  const currentPathArray =
    currentPath && currentPath !== "/" ? currentPath.split("/") : [];
  return currentPathArray.map((p, index) => ({
    item: p,
    path: currentPathArray.slice(0, index + 1).join("/"),
    title: `Browse ${currentPathArray.slice(0, index + 1).join("/")}`,
    r: sampleRelationshipId,
    childRoot: false
  }));
};

const mapToBreadCrumbItems = (
  sourceFileName,
  fetchedRelationshipPaths = [],
  sampleRelationshipId,
  currentPath = "",
  sourceHash
) => {
  // map root file
  const rootBreadcrumbItem = mapRootFile(sourceFileName);

  // map all fetched relationship paths
  const fetchedRelationshipBreadcrumbItems = mapFetchedRelationshipPaths(
    fetchedRelationshipPaths,
    sampleRelationshipId,
    sourceHash
  );
  // map currently navigated folders
  const currentPathBreadcrumbItems = mapCurrentFilePath(
    currentPath,
    sampleRelationshipId
  );
  return [
    rootBreadcrumbItem,
    ...fetchedRelationshipBreadcrumbItems,
    ...currentPathBreadcrumbItems
  ];
};

const NavBreadcrumb = ({
  sourceFileName,
  relationshipPaths = [],
  currentPath,
  sampleRelationshipID,
  sourceHash,
  className,
  innerBreadcrumbClassName,
  onItemClick,
  "data-testid": dataTestId
}) => {
  const breadcrumbItems = mapToBreadCrumbItems(
    sourceFileName,
    relationshipPaths,
    sampleRelationshipID,
    currentPath,
    sourceHash
  );

  return (
    <Breadcrumb
      className={`extracted-files-breadcrumb ${className || ""}`}
      listProps={{ className: innerBreadcrumbClassName }}
      data-testid={dataTestId}
    >
      {breadcrumbItems
        .filter(p => p.path)
        .map((item, index) => {
          const lastPath = index === breadcrumbItems.length - 1;
          const itemTestId = getBreadcrumbItemTestId(
            dataTestId,
            index,
            lastPath
          );
          return (
            <OverlayTrigger
              key={`breadcrumb-overlay-${index}`}
              placement="bottom"
              overlay={
                <Tooltip
                  className="breadcrumb-tooltip"
                  id={`${new Date().getTime()}`}
                  data-testid={itemTestId ? `${itemTestId}_tooltip` : null}
                >
                  {item.title}
                </Tooltip>
              }
            >
              <BreadcrumbItem
                key={`breadcrumb-item-${item.path}`}
                active={lastPath}
                onClick={() => {
                  if (!lastPath) {
                    onItemClick({ value: item });
                  }
                }}
                data-testid={itemTestId}
              >
                {index === 0 || lastPath || item.childRoot
                  ? getFileNameDisplay(item)
                  : "..."}
              </BreadcrumbItem>
            </OverlayTrigger>
          );
        })}
    </Breadcrumb>
  );
};

export default NavBreadcrumb;

NavBreadcrumb.propTypes = {
  sourceFileName: PropTypes.string.isRequired,
  relationshipPaths: PropTypes.arrayOf(
    PropTypes.shape({
      hash_value: PropTypes.string,
      path: PropTypes.string,
      r: PropTypes.number
    })
  ).isRequired,
  sourceHash: PropTypes.string,
  currentPath: PropTypes.string,
  sampleRelationshipID: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string
  ]).isRequired,
  className: PropTypes.string,
  onItemClick: PropTypes.func.isRequired,
  innerBreadcrumbClassName: PropTypes.string,
  "data-testid": PropTypes.string
};
